'use client'

import {
  ArrowsClockwiseIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  QuestionIcon,
} from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import Button from '@/src/components/core/Button'
import IconGradientWrapper from '@/src/components/core/icons/IconGradientWrapper'
import Container from '@/src/components/layout/Container'

const FlipCard = ({
  question,
  answer,
}: FahrlehrerCh.TeacherProfileQuestionAnswer) => {
  const [flipped, setFlipped] = useState(false)

  return (
    <div className="perspective-midrange">
      <button
        onClick={() => setFlipped((f) => !f)}
        className="relative block min-h-60 w-full"
      >
        <div
          className={clsx(
            'absolute inset-0 origin-center overflow-hidden rounded-xl border border-gray-150 bg-linear-to-b from-gray-50 to-gray-100 transition-transform duration-300 backface-hidden',
            { '-rotate-y-180': flipped }
          )}
        >
          <div className="absolute -top-6 left-26 opacity-20">
            <IconGradientWrapper from="#DEE1F4" to="#DEE1F400">
              <QuestionIcon size={425} />
            </IconGradientWrapper>
          </div>
          <div className="relative flex h-full flex-col items-center px-8 pt-16 pb-6 text-center shadow-xs">
            <p className="text-sm font-bold text-blue-700">Frage</p>
            <p className="mt-1 font-black">{question}</p>
            <div className="mt-auto flex items-center gap-2 pt-6 text-sm font-bold text-gray-800">
              <ArrowsClockwiseIcon size={20} />
              <p>Antwort zeigen</p>
            </div>
          </div>
        </div>
        <div
          className={clsx(
            'absolute inset-0 origin-center overflow-hidden rounded-xl border border-gray-150 bg-linear-to-b from-blue-600 to-blue-700 transition-transform duration-300 backface-hidden',
            { 'rotate-y-180': !flipped }
          )}
        >
          <div className="absolute -top-6 left-26 opacity-15">
            <IconGradientWrapper from="#0D0B3F" to="#0D0B3F00">
              <CheckCircleIcon size={425} />
            </IconGradientWrapper>
          </div>
          <div className="relative z-10 flex h-full flex-col items-center justify-center px-8 py-12 text-center shadow-xs">
            <p className="text-sm font-bold text-white/60">Antwort</p>
            <p className="mt-1 font-black text-white">{answer}</p>
          </div>
        </div>
      </button>
    </div>
  )
}

const QuestionTopics = ({
  name,
  questionAnswers,
  conversationTopics,
}: FahrlehrerCh.TeacherProfile) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 768px)': { skipSnaps: true },
    },
  })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('select', onSelect).on('reInit', onSelect)
  }, [emblaApi, onSelect])

  if (questionAnswers.length === 0 && conversationTopics.length === 0)
    return null

  return (
    <section id="questions">
      {questionAnswers.length > 0 && (
        <>
          <Container className="mt-20 md:mt-24">
            <h2 className="text-4xl font-black md:text-10xl">
              Fragen an {name}
            </h2>
            <p className="mt-4 text-gray-800 md:text-lg">
              Damit du {name} besser kennenlernen kannst.
            </p>
          </Container>
          <div
            ref={emblaRef}
            className="hidden overflow-hidden pt-6 pb-10 md:block"
          >
            <div className="flex select-none">
              {questionAnswers.map((qa, i) => (
                <div
                  key={i}
                  className="ml-5 min-w-0 flex-[0_0_calc(100%-40px)] last:mr-5 min-[1342px]:first:ml-[calc((100%-1280px)/2)] min-[1342px]:last:mr-[calc((100%-1280px)/2)]! md:ml-6 md:flex-[0_0_400px] md:last:mr-6"
                >
                  <FlipCard {...qa} />
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 grid gap-4 md:hidden">
            {questionAnswers.map((qa, i) => (
              <div key={i} className="px-5">
                <FlipCard {...qa} />
              </div>
            ))}
          </div>
          <Container>
            <div className="hidden justify-end gap-3 md:flex">
              <Button
                icon={<CaretLeftIcon weight="bold" />}
                size="icon-small"
                version="gray-outlined"
                onClick={() => emblaApi?.scrollPrev()}
                disabled={!canScrollPrev}
                aria-label="Vorherige Karte"
              />
              <Button
                icon={<CaretRightIcon weight="bold" />}
                size="icon-small"
                version="gray-outlined"
                onClick={() => emblaApi?.scrollNext()}
                disabled={!canScrollNext}
                aria-label="Nächste Karte"
              />
            </div>
          </Container>
        </>
      )}

      {conversationTopics.length > 0 && (
        <Container className={questionAnswers.length > 0 ? 'mt-12' : 'pt-12'}>
          <h3 className="text-4xl font-black">Gesprächsthemen mit {name}</h3>
          <div className="-mx-5 mt-4 flex gap-3 overflow-x-auto md:mx-0 md:flex-wrap md:overflow-x-visible">
            {conversationTopics.map((topic) => (
              <div
                key={topic.topic}
                className="flex shrink-0 items-center gap-2 rounded-xl border border-gray-300 px-5 py-3 first:ml-5 last:mr-5 md:first:ml-0 md:last:mr-0"
              >
                <span className="text-xl leading-none">{topic.emoji}</span>
                <span className="font-bold whitespace-nowrap">
                  {topic.topic}
                </span>
              </div>
            ))}
          </div>
        </Container>
      )}
    </section>
  )
}

export default QuestionTopics
